import { ApiError, type api, type Me } from "./api.ts";

/** A switch restores usable UI only after the browser's session is known. */
export type OrganizationSelection =
  | {
      readonly status: "confirmed";
      readonly me: Me;
      readonly notice: string | null;
    }
  | { readonly status: "signed_out" }
  | { readonly status: "unavailable"; readonly message: string }
  | { readonly status: "cancelled" };

/**
 * Select a team, then reconcile any uncertain outcome through a session read.
 * Set-Cookie can reach the browser before a response body fails. Never retry
 * the mutation with the old CSRF token or assume the old team is still selected.
 */
export async function selectOrganizationSession(
  request: typeof api,
  organizationId: string,
  csrf: string,
): Promise<OrganizationSelection> {
  try {
    const me = await request<Me>("/v1/me", {
      method: "PATCH",
      csrf,
      body: { selected_organization_id: organizationId },
    });
    return { status: "confirmed", me, notice: null };
  } catch (cause) {
    if (isCancellation(cause)) return { status: "cancelled" };
    try {
      const me = await request<Me>("/v1/me");
      return {
        status: "confirmed",
        me,
        notice:
          me.selected_organization_id === organizationId
            ? null
            : `Could not switch teams. ${cause instanceof ApiError ? cause.message : "Please try again."}`,
      };
    } catch (recovery) {
      if (isCancellation(recovery)) return { status: "cancelled" };
      if (recovery instanceof ApiError && recovery.status === 401) {
        return { status: "signed_out" };
      }
      return {
        status: "unavailable",
        message:
          "Could not confirm your current team. Try again to reload your workspace before making changes.",
      };
    }
  }
}

function isCancellation(cause: unknown): boolean {
  return cause instanceof ApiError && cause.code === "request_cancelled";
}
